import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material'
import ArrowForward from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import apiService from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import Error404 from './Error404'

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

const getProxiedUrl = (originalUrl) => {
  if (!originalUrl || typeof originalUrl !== 'string') {
    return ''
  }
  const trimmed = originalUrl.trim()
  if (!trimmed || trimmed.startsWith('/') || trimmed.startsWith('data:')) {
    return trimmed
  }
  if (trimmed.includes('/api/v1/profile/avatar?url=')) {
    return trimmed
  }
  const backendBase =
    (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080').replace(
      /\/$/,
      ''
    )
  return `${backendBase}/api/v1/profile/avatar?url=${encodeURIComponent(trimmed)}`
}

const formatDate = (dateInput) => {
  if (!dateInput) return 'Date unavailable'
  const date = new Date(dateInput)
  if (Number.isNaN(date.getTime())) return 'Date unavailable'
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const PublicProfile = () => {
  const { username } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const normalizedUsername = useMemo(() => {
    if (!username || typeof username !== 'string') {
      return ''
    }
    const trimmed = username.trim()
    if (!trimmed.startsWith('@')) {
      return ''
    }
    return trimmed.slice(1)
  }, [username])

  const [profile, setProfile] = useState(null)
  const [blogs, setBlogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [followLoading, setFollowLoading] = useState(false)
  const [connectionsOpen, setConnectionsOpen] = useState(false)
  const [connectionsType, setConnectionsType] = useState('followers')
  const [connections, setConnections] = useState([])
  const [connectionsPage, setConnectionsPage] = useState(1)
  const [connectionsHasMore, setConnectionsHasMore] = useState(false)
  const [connectionsLoading, setConnectionsLoading] = useState(false)
  const [connectionsLoadingMore, setConnectionsLoadingMore] = useState(false)
  const [connectionsError, setConnectionsError] = useState('')

  const fetchProfileAndBlogs = useCallback(
    async (pageNum = 1, append = false) => {
      if (!normalizedUsername) return
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      setError('')

      try {
        const [profileResponse, blogsResponse] = await Promise.all([
          apiService.getPublicProfileByUsername(normalizedUsername, {
            bypassCache: true,
            skipCache: true,
          }),
          apiService.getBlogsByUsername(normalizedUsername, pageNum, 6, {
            bypassCache: true,
            skipCache: true,
          }),
        ])

        setProfile(profileResponse)
        setBlogs((prev) =>
          append ? [...prev, ...(blogsResponse?.content || [])] : blogsResponse?.content || []
        )
        setHasMore(pageNum < (blogsResponse?.totalPages || 0))
      } catch (err) {
        setError(err?.userMessage || 'Unable to load this profile right now.')
        if (!append) {
          setBlogs([])
          setProfile(null)
        }
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [normalizedUsername]
  )

  const fetchConnections = useCallback(
    async (type, pageNum = 1, append = false) => {
      if (!normalizedUsername) return
      if (append) {
        setConnectionsLoadingMore(true)
      } else {
        setConnectionsLoading(true)
        setConnectionsError('')
      }

      try {
        const response =
          type === 'following'
            ? await apiService.getFollowingByUsername(normalizedUsername, pageNum, 20, {
                bypassCache: true,
                skipCache: true,
              })
            : await apiService.getFollowersByUsername(normalizedUsername, pageNum, 20, {
                bypassCache: true,
                skipCache: true,
              })

        const content = response?.content || []
        setConnections((prev) => (append ? [...prev, ...content] : content))
        setConnectionsPage(pageNum)
        setConnectionsHasMore(pageNum < (response?.totalPages || 0))
      } catch (err) {
        setConnectionsError(
          err?.userMessage || 'Unable to load this list right now.'
        )
        if (!append) {
          setConnections([])
        }
      } finally {
        setConnectionsLoading(false)
        setConnectionsLoadingMore(false)
      }
    },
    [normalizedUsername]
  )

  useEffect(() => {
    if (!normalizedUsername) {
      setLoading(false)
      return
    }
    setPage(1)
    fetchProfileAndBlogs(1, false)
  }, [fetchProfileAndBlogs, normalizedUsername])

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return
    const nextPage = page + 1
    setPage(nextPage)
    fetchProfileAndBlogs(nextPage, true)
  }

  const handleOpenBlog = (slug) => {
    if (isAuthenticated) {
      navigate(`/blogs/${slug}`)
      return
    }
    sessionStorage.setItem('redirectPath', `/blogs/${slug}`)
    navigate('/login')
  }

  const handleToggleFollow = async () => {
    if (!profile || profile.currentUserProfile || followLoading) {
      return
    }

    if (!isAuthenticated) {
      sessionStorage.setItem('redirectPath', `/@${normalizedUsername}`)
      navigate('/login')
      return
    }

    setFollowLoading(true)
    setError('')
    try {
      const response = profile.followedByCurrentUser
        ? await apiService.unfollowUserByUsername(normalizedUsername)
        : await apiService.followUserByUsername(normalizedUsername)

      setProfile((prev) =>
        prev
          ? {
              ...prev,
              followedByCurrentUser: Boolean(response?.following),
              followersCount:
                typeof response?.followersCount === 'number'
                  ? response.followersCount
                  : prev.followersCount,
              followingCount:
                typeof response?.followingCount === 'number'
                  ? response.followingCount
                  : prev.followingCount,
            }
          : prev
      )
    } catch (err) {
      setError(err?.userMessage || 'Unable to update follow state right now.')
    } finally {
      setFollowLoading(false)
    }
  }

  const openConnections = (type) => {
    setConnectionsType(type)
    setConnectionsOpen(true)
    setConnectionsPage(1)
    setConnections([])
    fetchConnections(type, 1, false)
  }

  const closeConnections = () => {
    setConnectionsOpen(false)
    setConnectionsError('')
  }

  const loadMoreConnections = () => {
    if (connectionsLoadingMore || !connectionsHasMore) return
    fetchConnections(connectionsType, connectionsPage + 1, true)
  }

  const handleOpenConnectionProfile = (connectionUsername) => {
    if (!connectionUsername) return
    setConnectionsOpen(false)
    navigate(`/@${connectionUsername}`)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!normalizedUsername) {
    return <Error404 />
  }

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 8 }}>
      {error && (
        <Container maxWidth='lg' sx={{ pt: 3 }}>
          <Alert severity='error'>{error}</Alert>
        </Container>
      )}

      {!error && profile && (
        <>
          <Box
            sx={{
              height: { xs: 170, md: 230 },
              backgroundImage: profile.coverPhotoUrl
                ? `url(${profile.coverPhotoUrl})`
                : `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.hover} 100%)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderBottom: `5px solid ${themeColors.secondary}`,
            }}
          />

          <Container maxWidth='lg'>
            <Box
              sx={{
                mt: { xs: -6, md: -8 },
                mb: 4,
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                backgroundColor: '#ffffff',
                boxShadow: '0 14px 28px rgba(15, 23, 42, 0.08)',
                border: '1px solid #e2e8f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Avatar
                  src={getProxiedUrl(profile.profilePictureUrl) || undefined}
                  alt={profile.name || profile.username}
                  sx={{
                    width: { xs: 72, md: 92 },
                    height: { xs: 72, md: 92 },
                    border: `3px solid ${themeColors.secondary}`,
                    bgcolor: themeColors.primary,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant='h5' sx={{ fontWeight: 700, color: themeColors.primary }}>
                    {profile.name || profile.username}
                  </Typography>
                  <Typography variant='body1' sx={{ color: '#475569', fontWeight: 600 }}>
                    @{profile.username}
                  </Typography>
                  {profile.bio && (
                    <Typography variant='body2' sx={{ color: '#475569', mt: 1 }}>
                      {profile.bio}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {!profile.currentUserProfile && (
                    <Button
                      variant={profile.followedByCurrentUser ? 'outlined' : 'contained'}
                      onClick={handleToggleFollow}
                      disabled={followLoading}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        backgroundColor: profile.followedByCurrentUser
                          ? 'transparent'
                          : themeColors.primary,
                        color: profile.followedByCurrentUser
                          ? themeColors.primary
                          : '#ffffff',
                        borderColor: themeColors.primary,
                        '&:hover': {
                          backgroundColor: profile.followedByCurrentUser
                            ? '#f1f5f9'
                            : themeColors.hover,
                          borderColor: themeColors.hover,
                        },
                      }}
                    >
                      {followLoading
                        ? 'Updating...'
                        : profile.followedByCurrentUser
                        ? 'Following'
                        : 'Follow'}
                    </Button>
                  )}
                  <Button
                    variant='text'
                    onClick={() => openConnections('following')}
                    sx={{ textTransform: 'none', color: themeColors.primary, fontWeight: 600 }}
                  >
                    {profile.followingCount || 0} Following
                  </Button>
                  <Button
                    variant='text'
                    onClick={() => openConnections('followers')}
                    sx={{ textTransform: 'none', color: themeColors.primary, fontWeight: 600 }}
                  >
                    {profile.followersCount || 0} Followers
                  </Button>
                  <Typography variant='body2' sx={{ color: themeColors.primary, fontWeight: 600 }}>
                    {profile.contributionsCount || 0} Contributions
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography
              variant='h5'
              sx={{
                fontWeight: 700,
                color: themeColors.primary,
                mb: 2,
              }}
            >
              Contributions
            </Typography>

            <Grid container spacing={2}>
              {blogs.map((blog) => (
                <Grid item xs={12} sm={6} md={4} key={blog.id || blog.slug}>
                  <Card
                    onClick={() => handleOpenBlog(blog.slug)}
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      transition: '0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.1)',
                        borderColor: themeColors.secondary,
                      },
                    }}
                  >
                    {blog.imageUrl && (
                      <CardMedia component='img' image={blog.imageUrl} height='170' alt={blog.title || 'Blog'} />
                    )}
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        {blog.category && (
                          <Box sx={{ mb: 1.5 }}>
                            <Chip
                              label={blog.category}
                              size='small'
                              sx={{
                                backgroundColor: 'rgba(255, 207, 81, 0.14)',
                                color: themeColors.primary,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                              }}
                            />
                          </Box>
                        )}
                        <Typography variant='h6' sx={{ fontWeight: 700, color: themeColors.primary, mb: 1 }}>
                          {blog.title || 'Untitled'}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: '#64748b',
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {blog.excerpt || blog.description || 'No preview available.'}
                        </Typography>
                        <Typography variant='caption' sx={{ color: '#64748b' }}>
                          {formatDate(blog.publishedDate)}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Button
                          size='small'
                          endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                          onClick={(event) => {
                            event.stopPropagation()
                            handleOpenBlog(blog.slug)
                          }}
                          sx={{
                            padding: '6px 16px',
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            backgroundColor: themeColors.secondary,
                            color: themeColors.primary,
                            '&:hover': {
                              backgroundColor: '#f5c842',
                            },
                          }}
                        >
                          {isAuthenticated ? 'Read More' : 'Login to Read'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {!loading && blogs.length === 0 && (
              <Typography variant='body1' sx={{ mt: 3, color: '#475569' }}>
                No contributions published yet.
              </Typography>
            )}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant='contained'
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  sx={{
                    backgroundColor: themeColors.primary,
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: themeColors.hover },
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </Container>
        </>
      )}

      <Dialog
        open={connectionsOpen}
        onClose={closeConnections}
        fullWidth
        maxWidth='sm'
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${themeColors.secondary}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: themeColors.primary,
            fontWeight: 700,
          }}
        >
          {connectionsType === 'followers' ? 'Followers' : 'Following'}
          <IconButton onClick={closeConnections} size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {connectionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {connectionsError && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {connectionsError}
                </Alert>
              )}

              {!connectionsError && connections.length === 0 && (
                <Typography variant='body2' sx={{ color: '#64748b' }}>
                  {connectionsType === 'followers'
                    ? 'No followers yet.'
                    : 'Not following anyone yet.'}
                </Typography>
              )}

              {!connectionsError && connections.length > 0 && (
                <List disablePadding>
                  {connections.map((connection, index) => (
                    <Box key={`${connection.username}-${index}`}>
                      <ListItem
                        secondaryAction={
                          <Button
                            size='small'
                            variant='text'
                            sx={{
                              textTransform: 'none',
                              color: themeColors.primary,
                              fontWeight: 600,
                            }}
                            onClick={() =>
                              handleOpenConnectionProfile(connection.username)
                            }
                          >
                            View
                          </Button>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={getProxiedUrl(connection.profilePictureUrl) || undefined}
                            alt={connection.name || connection.username}
                            sx={{
                              bgcolor: themeColors.primary,
                              width: 42,
                              height: 42,
                            }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={connection.name || connection.username}
                          secondary={`@${connection.username}`}
                          primaryTypographyProps={{
                            sx: {
                              color: themeColors.primary,
                              fontWeight: 700,
                            },
                          }}
                          secondaryTypographyProps={{
                            sx: { color: '#64748b' },
                          }}
                        />
                      </ListItem>
                      {index < connections.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              )}

              {connectionsHasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant='outlined'
                    onClick={loadMoreConnections}
                    disabled={connectionsLoadingMore}
                    sx={{
                      textTransform: 'none',
                      color: themeColors.primary,
                      borderColor: themeColors.primary,
                      '&:hover': {
                        borderColor: themeColors.hover,
                        backgroundColor: '#f8fafc',
                      },
                    }}
                  >
                    {connectionsLoadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default PublicProfile
